import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import { DenyReason, DenyEntryPayload, DenyEntry } from '../../types/denyTypes';

interface DenyState {
  denyReasons: ApiResponse<DenyReason>;
  selectedDenyEntry: DenyEntry | null;
  fetchDenyReasonsLoading: boolean;
  denyEntryLoading: boolean;
  describeDenyReasonLoading: boolean;
  describeDeniedEntryLoading: boolean;
  error: string | null;
}

const initialState: DenyState = {
  denyReasons: {
    data: [],
    meta: null,
    totalCount: 0,
    filteredCount: 0,
  },
  selectedDenyEntry: null,
  fetchDenyReasonsLoading: false,
  describeDenyReasonLoading: false,
  describeDeniedEntryLoading: false,
  denyEntryLoading: false,
  error: null,
};

const denySlice = createSlice({
  name: 'deny',
  initialState,
  reducers: {
    fetchDenyReasonsRequest(state, _action: PayloadAction<{ orgId: number }>) {
      return { ...state, fetchDenyReasonsLoading: true, error: null };
    },
    describeDenyReasonRequest(
      state,
      _action: PayloadAction<{ orgId: number; reasonId: number }>,
    ) {
      return { ...state, describeDenyReasonLoading: true, error: null };
    },
    describeDeniedEntryRequest(
      state,
      _action: PayloadAction<{ orgId: number; visitId: number }>,
    ) {
      return { ...state, describeDeniedEntryLoading: true, error: null };
    },
    denyEntryRequest(
      state,
      _action: PayloadAction<{
        orgId: number;
        visitId: number;
        denyEntry: Partial<DenyEntryPayload>;
      }>,
    ) {
      return { ...state, denyEntryLoading: true, error: null };
    },

    fetchDenyReasonsSuccess(
      state,
      action: PayloadAction<ApiResponse<DenyReason>>,
    ) {
      return {
        ...state,
        fetchDenyReasonsLoading: false,
        denyReasons: action.payload,
      };
    },
    describeDenyReasonSuccess(state) {
      return { ...state, describeDenyReasonLoading: false };
    },
    describeDeniedEntrySuccess(state, action: PayloadAction<DenyEntry>) {
      return {
        ...state,
        describeDeniedEntryLoading: false,
        selectedDenyEntry: action.payload,
      };
    },
    denyEntrySuccess(state) {
      return { ...state, denyEntryLoading: false };
    },
    denyApiFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        fetchDenyReasonsLoading: false,
        describeDenyReasonLoading: false,
        describeDeniedEntryLoading: false,
        denyEntryLoading: false,
        error: action.payload,
      };
    },

    clearDenyReasons(state) {
      return { ...state, denyReasons: initialState.denyReasons };
    },
    clearError(state) {
      return { ...state, error: null };
    },
    clearDenyState() {
      return initialState;
    },
  },
});

export const {
  fetchDenyReasonsRequest,
  describeDenyReasonRequest,
  describeDeniedEntryRequest,
  denyEntryRequest,
  fetchDenyReasonsSuccess,
  describeDenyReasonSuccess,
  describeDeniedEntrySuccess,
  denyEntrySuccess,
  denyApiFailure,
  clearDenyReasons,
  clearError,
  clearDenyState,
} = denySlice.actions;

export default denySlice.reducer;
